import React from "react";
import { DISCORD_URL, DISCORD_AVAILABLE } from "@/lib/community";

/**
 * Discord community CTA. Renders as a button/link with a distinctive Discord-purple gradient.
 * If REACT_APP_DISCORD_URL is not set, shows a disabled "Bientôt" state with a soft glow.
 *
 * Variants:
 *   - compact (sidebar / topbar) — small pill
 *   - default (footer / hero)     — medium button
 *   - large   (community section) — big hero-style card
 */
export default function DiscordCTA({ variant = "default", className = "", label }) {
  const base = "inline-flex items-center gap-2 font-medium transition-all";
  const styleByVariant = {
    compact: "px-3 py-2 rounded-md text-sm w-full justify-center",
    default: "px-4 py-2 rounded-lg text-sm",
    large:   "px-6 py-3 rounded-xl text-base",
  };
  const enabled = DISCORD_AVAILABLE
    ? "bg-[#5865F2]/20 text-[#c7cbfa] border border-[#5865F2]/40 hover:bg-[#5865F2]/30 hover:border-[#5865F2] hover:shadow-[0_0_20px_rgba(88,101,242,0.3)]"
    : "bg-white/[0.04] text-white/50 border border-white/10 cursor-not-allowed";

  const finalLabel = label ?? (DISCORD_AVAILABLE ? "Discord communauté" : "Discord · Bientôt");
  const cls = `${base} ${styleByVariant[variant]} ${enabled} ${className}`;

  if (!DISCORD_AVAILABLE) {
    return (
      <span className={cls} data-testid="discord-cta-disabled" title="La communauté Discord ouvre bientôt">
        <DiscordIcon size={variant === "large" ? 22 : 16} />
        {finalLabel}
      </span>
    );
  }
  return (
    <a
      href={DISCORD_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cls}
      data-testid="discord-cta-link"
    >
      <DiscordIcon size={variant === "large" ? 22 : 16} />
      {finalLabel}
    </a>
  );
}

function DiscordIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.667 12.667 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.735 19.735 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.083.083 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.995a.076.076 0 0 0-.041-.106 13.098 13.098 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.332.955-2.418 2.157-2.418 1.21 0 2.176 1.095 2.157 2.418 0 1.334-.955 2.42-2.157 2.42zm7.977 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.332.955-2.418 2.157-2.418 1.21 0 2.176 1.095 2.157 2.418 0 1.334-.946 2.42-2.157 2.42z" />
    </svg>
  );
}
